import React, { useState } from 'react';
import { Sparkles, Heart, Target, Zap, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

const ArchetypeResults = ({ blueprint, userName, onContinue }) => {
  const [showFullBlueprint, setShowFullBlueprint] = useState(false);

  const archetypeEmojis = {
    "Nurturer": "💖",
    "Dreamer": "🌙",
    "Phoenix": "🔥",
    "Radiant": "🌟",
    "Visionary": "✨",
    "Healer": "🌸",
    "Warrior": "⚔️",
    "Muse": "🎶"
  };

  const emoji = archetypeEmojis[blueprint.primary_archetype] || blueprint.emoji;
  const secondaryText = blueprint.secondary_archetype 
    ? ` with ${blueprint.secondary_archetype} tendencies`
    : '';

  // Dimension bars with colors
  const dimensionInfo = {
    "self_worth": { label: "Self-Worth", color: "bg-rose-500" },
    "clarity": { label: "Clarity", color: "bg-blue-500" },
    "consistency": { label: "Consistency", color: "bg-green-500" },
    "boundaries": { label: "Boundaries", color: "bg-purple-500" },
    "drive": { label: "Drive", color: "bg-orange-500" },
    "resilience": { label: "Resilience", color: "bg-red-500" }
  };

  const getDimensionWidth = (score) => {
    // Score range is typically -6 to +6, normalize to 0-100%
    const normalized = ((score + 6) / 12) * 100;
    return Math.max(10, Math.min(100, normalized));
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-20">
      <div className="w-full max-w-4xl">
        {!showFullBlueprint ? (
          // Archetype Reveal
          <Card className="p-8 md:p-12 bg-gradient-to-br from-rose-50 via-white to-pink-50 border-2 border-rose-200 shadow-2xl">
            {/* Celebration Header */}
            <div className="text-center mb-8">
              <div className="inline-block animate-bounce mb-4">
                <Sparkles className="w-16 h-16 text-yellow-500" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                🎉 Meet Your Blueprint
              </h1>
              <p className="text-lg text-gray-600">
                {userName}, here's who you are and where you're going
              </p>
            </div>

            {/* Archetype Card */}
            <div className="bg-gradient-to-br from-rose-400 to-rose-600 rounded-3xl p-8 md:p-12 text-white text-center mb-8 shadow-xl">
              <div className="text-7xl md:text-8xl mb-6 animate-pulse">
                {emoji}
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                The {blueprint.primary_archetype}
              </h2>
              {blueprint.secondary_archetype && (
                <p className="text-xl text-rose-100 mb-4">
                  {secondaryText}
                </p>
              )}
              <p className="text-2xl font-semibold text-yellow-200 mb-6">
                {blueprint.tagline}
              </p>
              <p className="text-lg text-rose-50 leading-relaxed max-w-2xl mx-auto">
                {blueprint.description}
              </p>
            </div>

            {/* Quick Overview */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="p-6 bg-white border-2 border-rose-100">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Target className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Where You Are Now</h3>
                    <p className="text-sm text-gray-700">{blueprint.current_state}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white border-2 border-rose-100">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Where You're Going</h3>
                    <p className="text-sm text-gray-700">{blueprint.future_state}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Button
                onClick={() => setShowFullBlueprint(true)}
                className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white px-8 py-6 text-lg font-semibold shadow-lg"
              >
                View My Full Blueprint & Roadmap
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </Card>
        ) : (
          // Full Blueprint
          <div className="space-y-6">
            {/* Header */}
            <Card className="p-6 bg-gradient-to-r from-rose-500 to-rose-600 text-white border-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-5xl">{emoji}</div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold">
                      Your Personalized Blueprint
                    </h1>
                    <p className="text-rose-100">
                      The {blueprint.primary_archetype}{secondaryText}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Dimension Scores */}
            <Card className="p-6 md:p-8 bg-white border-2 border-rose-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Zap className="w-6 h-6 text-yellow-500 mr-2" />
                Your Growth Dimensions
              </h2>
              <div className="space-y-4">
                {Object.entries(blueprint.dimension_scores).map(([dimension, score]) => {
                  const info = dimensionInfo[dimension];
                  return (
                    <div key={dimension}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">
                          {info.label}
                        </span>
                        <span className="text-xs text-gray-600">
                          {score > 0 ? '+' : ''}{score}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`${info.color} h-3 rounded-full transition-all duration-1000`}
                          style={{ width: `${getDimensionWidth(score)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-4">
                These dimensions show your current patterns and growth opportunities
              </p>
            </Card>

            {/* 4Rs Roadmap */}
            <Card className="p-6 md:p-8 bg-white border-2 border-rose-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Heart className="w-6 h-6 text-rose-500 mr-2" />
                Your NeuroLove Roadmap
              </h2>

              {/* Reflect */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-blue-700 mb-3">
                  🔍 Reflect (Prompts to explore)
                </h3>
                <ul className="space-y-2">
                  {blueprint.roadmap.reflect.map((prompt, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      <span className="text-gray-700">{prompt}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Reframe */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">
                  ✨ Reframe (Affirmations to practice)
                </h3>
                <ul className="space-y-2">
                  {blueprint.roadmap.reframe.map((affirmation, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      <span className="text-gray-700 italic">"{affirmation}"</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Rewire */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-green-700 mb-3">
                  🌱 Rewire (Micro-actions to take)
                </h3>
                <ul className="space-y-2">
                  {blueprint.roadmap.rewire.map((action, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      <span className="text-gray-700">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Reinforce */}
              <div>
                <h3 className="text-lg font-bold text-orange-700 mb-3">
                  💪 Reinforce (Weekly practice)
                </h3>
                <p className="text-gray-700 flex items-start">
                  <span className="text-orange-500 mr-2">•</span>
                  {blueprint.roadmap.reinforce}
                </p>
              </div>
            </Card>

            {/* Save & Continue */}
            <div className="text-center">
              <Button
                onClick={onContinue}
                className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white px-8 py-6 text-lg font-semibold shadow-lg"
              >
                Continue to App Tour
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-sm text-gray-600 mt-4">
                Your blueprint is saved in your profile ✨
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchetypeResults;